package top.wcpe.allincore.ui.runtime.internal.time

/**
 * 平台中立的日历 / 格式化时间地基（FR-UI-021 批C 硬地基 B，经用户确认引入 kotlinx-datetime）。
 *
 * JVM actual 继续委托 java.time（pattern 全量支持、生产行为零变化）；
 * JS actual 委托 kotlinx-datetime（依赖仅进 jsMain，byUnicodePattern 覆盖常用 pattern 子集）。
 * 调用方：TimeDateApiInvoker（DSL `time.* / date.*` 内建），文件本体随批F 迁 common。
 * 时区口径：一律系统默认时区，与迁移前 [java.time.ZoneId.systemDefault] 一致。
 */

/** 日历单位：DSL `date.add / date.diff` 的合法单位。 */
internal enum class CalendarUnit { MILLIS, SECONDS, MINUTES, HOURS, DAYS, WEEKS, MONTHS, YEARS }

/** 当前本地时刻的秒字段（0-59）。 */
internal expect fun localTimeSecond(): Int

/** 当前本地时刻的分字段（0-59）。 */
internal expect fun localTimeMinute(): Int

/** 当前本地时刻的时字段（0-23）。 */
internal expect fun localTimeHour(): Int

/** 当前本地日期的 ISO-8601 文本（yyyy-MM-dd）。 */
internal expect fun localDateIso(): String

/** 当前本地日期的年。 */
internal expect fun localDateYear(): Int

/** 当前本地日期的月（1-12）。 */
internal expect fun localDateMonth(): Int

/** 当前本地日期的日（1-31）。 */
internal expect fun localDateDay(): Int

/** 当前本地日期的 ISO 星期序数（1=周一 … 7=周日）。 */
internal expect fun localDateDayOfWeek(): Int

/** 当前本地日期的 ISO-8601 周数（周基准年）。 */
internal expect fun localDateWeekOfYear(): Int

/** 当前本地日期在年内的序数（1 起）。 */
internal expect fun localDateDayOfYear(): Int

/** 按系统时区把 [epochMillis] 用 [pattern] 格式化；pattern 非法抛 [IllegalArgumentException]。 */
internal expect fun formatEpochMillis(epochMillis: Long, pattern: String): String

/**
 * 按 [pattern] 解析 [text] 为 epoch 毫秒：先按完整日期时间解析，失败再按纯日期补当天 00:00。
 * pattern 非法抛 [IllegalArgumentException]；文本解析失败返回 null。
 */
internal expect fun parseToEpochMillisOrNull(text: String, pattern: String): Long?

/** 系统时区日历加减：[epochMillis] 加 [amount] 个 [unit]（月 / 年按日历语义、月末钳制）。 */
internal expect fun addToEpochMillis(epochMillis: Long, amount: Long, unit: CalendarUnit): Long

/** 系统时区日历差值：从 [startMillis] 到 [endMillis] 相差多少个 [unit]（对齐 ChronoUnit.between 语义）。 */
internal expect fun diffEpochMillis(endMillis: Long, startMillis: Long, unit: CalendarUnit): Long
